/**
 * Renders a submitted Elevator Initial Survey to a PDF (base64 data URI).
 * Generated on the client at submit time and filed under the customer's
 * attachments so the response survives as a document, not just a form row.
 */

import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { ELEVATOR_SURVEY_FORM, ELEVATOR_SURVEY_PHOTOS } from '../data/elevatorSurveyForm';

const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN = 48;
const CONTENT_W = PAGE_W - MARGIN * 2;

const NAVY = rgb(0.16, 0.26, 0.37);
const GREY = rgb(0.35, 0.44, 0.55);
const BLACK = rgb(0.1, 0.11, 0.12);
const LINE = rgb(0.8, 0.83, 0.87);

export interface SurveyPdfPhoto {
  fieldId: string;
  /** Where to read the image bytes from; already uploaded by the time we get here. */
  url: string;
  fileName?: string;
}

export async function generateElevatorSurveyPdf(
  values: Record<string, string>,
  photos: SurveyPdfPhoto[],
  meta: { businessName: string }
): Promise<string> {
  const doc = await PDFDocument.create();
  const font = await doc.embedFont(StandardFonts.Helvetica);
  const bold = await doc.embedFont(StandardFonts.HelveticaBold);

  let page: PDFPage = doc.addPage([PAGE_W, PAGE_H]);
  let y = PAGE_H - MARGIN;

  const ensure = (needed: number) => {
    if (y - needed < MARGIN) {
      page = doc.addPage([PAGE_W, PAGE_H]);
      y = PAGE_H - MARGIN;
    }
  };

  const wrap = (value: string, f: PDFFont, size: number, maxW: number): string[] => {
    const words = String(value ?? '').split(/\s+/).filter(Boolean);
    if (words.length === 0) return [''];
    const lines: string[] = [];
    let cur = '';
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (f.widthOfTextAtSize(test, size) > maxW && cur) {
        lines.push(cur);
        cur = w;
      } else {
        cur = test;
      }
    }
    if (cur) lines.push(cur);
    return lines;
  };

  const heading = (s: string) => {
    ensure(28);
    y -= 8;
    page.drawText(s, { x: MARGIN, y: y - 12, size: 12, font: bold, color: NAVY });
    y -= 16;
    page.drawLine({ start: { x: MARGIN, y }, end: { x: PAGE_W - MARGIN, y }, thickness: 0.75, color: LINE });
    y -= 8;
  };

  const kv = (label: string, value: string) => {
    const labelW = 210;
    const labelLines = wrap(label, bold, 10, labelW - 8);
    const valueLines = wrap(value || '—', font, 10, CONTENT_W - labelW);
    const rows = Math.max(labelLines.length, valueLines.length);
    ensure(rows * 12 + 4);
    labelLines.forEach((line, idx) => {
      page.drawText(line, { x: MARGIN, y: y - 10 - idx * 12, size: 10, font: bold, color: GREY });
    });
    valueLines.forEach((line, idx) => {
      page.drawText(line, { x: MARGIN + labelW, y: y - 10 - idx * 12, size: 10, font, color: BLACK });
    });
    y -= rows * 12 + 2;
  };

  page.drawText('Elevator Initial Survey', { x: MARGIN, y: y - 20, size: 18, font: bold, color: NAVY });
  y -= 26;
  page.drawText(meta.businessName || '', { x: MARGIN, y: y - 12, size: 11, font, color: GREY });
  y -= 16;
  page.drawText(`Submitted ${new Date().toLocaleString()}`, { x: MARGIN, y: y - 12, size: 9, font, color: GREY });
  y -= 22;

  for (const section of ELEVATOR_SURVEY_FORM) {
    heading(section.title);
    for (const field of section.fields) kv(field.label, values[field.id] ?? '');
  }

  const uploaded = photos.filter((p) => p.url);
  heading('Elevator Service Provider — Photos');
  if (uploaded.length === 0) {
    ensure(16);
    page.drawText('No photos were provided.', { x: MARGIN, y: y - 10, size: 10, font, color: GREY });
    y -= 16;
  }

  for (const photo of uploaded) {
    const label = ELEVATOR_SURVEY_PHOTOS.find((p) => p.id === photo.fieldId)?.label ?? photo.fieldId;
    ensure(24);
    page.drawText(label, { x: MARGIN, y: y - 11, size: 10, font: bold, color: NAVY });
    y -= 18;

    try {
      const res = await fetch(photo.url);
      if (!res.ok) throw new Error('fetch failed');
      const bytes = new Uint8Array(await res.arrayBuffer());
      const type = (res.headers.get('content-type') || '').toLowerCase();
      const isPng = type.includes('png') || (bytes[0] === 0x89 && bytes[1] === 0x50);
      const img = isPng ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
      const scale = Math.min(CONTENT_W / img.width, 320 / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;
      ensure(h + 12);
      page.drawImage(img, { x: MARGIN, y: y - h, width: w, height: h });
      y -= h + 14;
    } catch {
      // A format pdf-lib cannot embed (HEIC, WebP) still reached the server; the
      // original file is in the customer's attachments, so just note it here.
      ensure(16);
      page.drawText(`(photo attached separately: ${photo.fileName || 'image'})`, {
        x: MARGIN,
        y: y - 10,
        size: 9,
        font,
        color: GREY,
      });
      y -= 18;
    }
  }

  return doc.saveAsBase64({ dataUri: true });
}
